import React, { useState, useEffect, useRef } from "react";
import { Button, Form } from "react-bootstrap";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { ToastContainer, toast } from "react-toastify";
import { BASE_URL, IMAGE_URL, security_key } from "../../../global";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import LinearProgress from "@mui/material/LinearProgress";

const EditMagazine = ({}) => {
    const { id } = useParams();
    const history = useNavigate();
    const [magazine, setMagazine] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [imageError, setImageError] = useState("");
    const formRef = useRef(null);
    const [magTypeOption, setMagTypeOption] = useState([
        { value: 2, label: "Manual" },
        { value: 3, label: "Feed" },
    ]);

    useEffect(() => {
        const fetchMagazine = async () => {
            try {
                const response = await axios.post(BASE_URL + "API/apicall", {
                    security_key: security_key,
                    pk_mag_key: id,
                    mode: "getMagazine",
                });
                const result = response.data;
                setMagazine(result);
            } catch (error) {
                toast.error("Error fetching magazine details.", { autoClose: 3000 });
            }
        };

        fetchMagazine();
    }, [id]);

    const initialValues = {
        pk_mag_key: magazine ? magazine.pk_mag_key : "",
        mag_type: magazine ? (magazine.mag_type === 1 ? "1" : magazine.mag_type === 2 ? "2" : magazine.mag_type === 3 ? "3" : "") : "",
        mag_name: magazine ? magazine.mag_name : "",
        img_to_rem: magazine ? magazine.mag_logo_mod : null,
        mag_link: magazine ? magazine.mag_link : "",
        mag_img: null,
        mag_logo_mod: magazine && magazine.mag_logo_mod ? IMAGE_URL + magazine.mag_logo_mod : "",
        updated_by: sessionStorage.getItem("pk_user_key"),
        fk_user_key: sessionStorage.getItem("pk_user_key"),
    };

    const basicSchema = Yup.object().shape({
        mag_type: Yup.string().required("Magazine type is required."),

        mag_name: Yup.string()
            .min(3, "Magazine Name must be at least 3 characters.")
            .max(250, "Magazine Name must be at most 250 characters.")
            .matches(/^[a-zA-Z\s]*$/, "Special characters are not allowed.")
            .matches(/^[^\s].*[^\s]$/, "No leading or trailing spaces are allowed.")
            .required("Magazine Name is required.")
            .test("unique-link", "Magazine Name is already existing in the database", async function (value) {
                if (!value) return true;
                return await checkMagazineName(value);
            }),
        mag_link: Yup.string().url("Invalid URL format.").required("Magazine Link is required."),
        mag_img: Yup.mixed()
            .nullable()
            .test("fileType", "Unsupported File Format", (value) => {
                if (!value) return true;
                const supportedFormats = ["image/jpeg", "image/png", "image/gif"];
                return value ? supportedFormats.includes(value.type) : false;
            })
            .test("fileSize", "File size must be less than 2MB", (value) => {
                if (!value) return true;
                const maxSize = 2 * 1024 * 1024; // 2MB in bytes
                return value ? value.size <= maxSize : false;
            }),
    });

    async function checkMagazineName(mag_name) {
        const response = await axios.post(BASE_URL + "API/apicall", {
            security_key: security_key,
            mode: "checkMagazineName",
            pk_mag_key: id,
            mag_name: mag_name,
        });

        const output = response.data;

        return output == 1 ? false : true;
    }

    const formik = useFormik({
        initialValues,
        validationSchema: basicSchema,
        enableReinitialize: true,
        onSubmit: async (formValues) => {
            toast.dismiss();

            setIsSubmitting(true);

            const response = await axios.post(
                BASE_URL + "API/apicall",
                {
                    security_key: security_key,
                    mode: "updateMagazine",
                    ...formValues,
                },
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            const output = response.data;
            if (output === 1) {
                toast.success("Updated successfully!");
                setTimeout(() => history("/admin/magazine-management"), 3000);
            } else {
                toast.error("Oops! Something went wrong.", {
                    autoClose: 3000,
                });
            }
            setTimeout(function () {
                setIsSubmitting(false);
            }, 3000);
        },
    });

    const { values, handleChange, handleBlur, touched, errors, handleSubmit, setFieldValue, setTouched } = formik;

    const handleFileChange = (event) => {
        const file = event.currentTarget.files[0];
        if (file) {
            // Check if the file is an image
            const fileType = file.type.split("/")[0];
            if (fileType !== "image") {
                setImageError("Unsupported file format. Please upload an image.");
                setFieldValue("mag_img", null);
                setImagePreview(null);
                return;
            } else {
                setImageError(""); // Clear any previous error

                setFieldValue("mag_img", file);

                const reader = new FileReader();
                reader.onload = () => {
                    setFieldValue("mag_logo_mod", reader.result);
                    setImagePreview(reader.result); // Preview image
                };
                reader.readAsDataURL(file);
            }
        }
    };

    return (
        <div className="container mt-2 bg-white p-4 mb-3">
            <ToastContainer position="top-center" />
            <h4 className="mb-2">Edit Magazine</h4>
            <Form className="mb-3" onSubmit={handleSubmit}>
                <Form.Group className="w-100 mb-3">
                    <Form.Label>
                        Magazine Type <span className="text-danger">*</span>
                    </Form.Label>
                    <Select
                        options={magTypeOption}
                        id="mag_type"
                        name="mag_type"
                        value={magTypeOption.find((option) => option.value && values.mag_type && option.value.toString() === values.mag_type.toString())}
                        onChange={(selectedOption) => {
                            const event = {
                                target: {
                                    name: "mag_type",
                                    value: selectedOption["value"],
                                },
                            };
                            handleChange(event);
                        }}
                        onBlur={(event) => {
                            handleBlur(event);
                            setTouched({ ...touched, mag_type: true });
                        }}
                    />

                    {errors.mag_type && touched.mag_type && <p className="text-danger">{errors.mag_type}</p>}
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>
                        Magazine Title <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                        type="text"
                        name="mag_name"
                        value={values.mag_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter magazine Name"
                    />
                    {errors.mag_name && touched.mag_name && <p className="text-danger">{errors.mag_name}</p>}
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>
                        Magazine Link <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                        type="text"
                        name="mag_link"
                        value={values.mag_link}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter magazine link"
                    />
                    {errors.mag_link && touched.mag_link && <p className="text-danger">{errors.mag_link}</p>}
                </Form.Group>
                <Form.Group controlId="formImage" className="mb-3">
                    <div className="position-relative" style={{ cursor: "pointer" }}>
                        <img src={values.mag_logo_mod} class="rounded mx-auto d-block mb-3" alt="..."></img>
                        <Form.Label className="position-absolute" style={{ top: "0px", right: "0px" }}>
                            <i class="bi bi-pencil-square"></i>
                        </Form.Label>
                    </div>
                    {/* <div
            className="dropzone border p-4 rounded"
            style={{ cursor: 'pointer', textAlign: 'center' }}
          > */}
                    <Form.Control
                        type="file"
                        onChange={handleFileChange}
                        onBlur={handleBlur}
                        accept="image/*"
                        style={{ display: "none" }} // Hide the default file input
                    />
                    {/* <div
              onClick={() => document.querySelector('input[type="file"]').click()} 
              style={{ cursor: 'pointer' }}
            >
              <p>Drag 'n' drop an image, or click to select one</p>
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                />
              ) : null}
            </div> */}
                    {/* </div> */}
                    {imageError && <p className="text-danger">{imageError}</p>}
                    {errors.mag_img && touched.mag_img && <p className="text-danger">{errors.mag_img}</p>}
                </Form.Group>
                <Button variant="dark" type="submit" ref={formRef} disabled={isSubmitting}>
                    Update Magazine
                    {isSubmitting && <LinearProgress />}
                </Button>
            </Form>
        </div>
    );
};

export default EditMagazine;
